import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
} from 'react-bootstrap';
import {
  FaCertificate,
  FaSearch,
  FaFilter,
  FaEye,
  FaDownload,
  FaTrash,
  FaCalendar,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import CertificateService from '../../../services/certificateService';
import BatchService from '../../../services/batchService';

const CertificateManagement = () => {
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterCertificates();
  }, [certificates, searchTerm, batchFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certResponse, batchResponse] = await Promise.all([
        CertificateService.getAllCertificates(),
        BatchService.getAllBatches()
      ]);
      setCertificates(certResponse.data || []);
      setBatches(batchResponse.data || []);
    } catch (error) {
      toast.error('Failed to load certificates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterCertificates = () => {
    let filtered = [...certificates];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.batch_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply batch filter
    if (batchFilter !== 'all') {
      filtered = filtered.filter(c => c.batch_id === parseInt(batchFilter));
    }

    setFilteredCertificates(filtered);
  };

  const handleViewCertificate = async (cert) => {
    try {
      await CertificateService.viewCertificate(cert.id);
    } catch (error) {
      toast.error('Failed to view certificate');
    }
  };

  const handleDownloadCertificate = async (cert) => {
    try {
      await CertificateService.downloadCertificate(cert.id);
      toast.success('Certificate download started');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const handleDeleteCertificate = async () => {
    if (!selectedCertificate) return;
    try {
      await CertificateService.deleteCertificate(selectedCertificate.id);
      toast.success('Certificate deleted successfully');
      loadData();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4" >
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>
              <FaCertificate className="me-2 text-warning" />
              Certificate Management
            </h2>
          </Col>
        </Row>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-primary text-white">
              <Card.Body>
                <h6>Total Certificates</h6>
                <h3>{certificates.length}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Issued This Month</h6>
                <h3>
                  {certificates.filter(c => {
                    const issueDate = new Date(c.issue_date);
                    const now = new Date();
                    return issueDate.getMonth() === now.getMonth() &&
                           issueDate.getFullYear() === now.getFullYear();
                  }).length}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Batches</h6>
                <h3>{new Set(certificates.map(c => c.batch_id)).size}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row>
              <Col md={8}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by student name or certificate number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={batchFilter}
                    onChange={(e) => setBatchFilter(e.target.value)}
                  >
                    <option value="all">All Batches</option>
                    {batches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Certificates Table */}
        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>Certificate No.</th>
                    <th>Batch</th>
                    <th>Course</th>
                    <th>Issue Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCertificates.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <h5 className="text-muted">No certificates found</h5>
                      </td>
                    </tr>
                  ) : (
                    filteredCertificates.map((cert, index) => (
                      <tr key={cert.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{cert.student_name}</strong>
                        </td>
                        <td>
                          <code>{cert.certificate_number}</code>
                        </td>
                        <td>{cert.batch_name}</td>
                        <td>{cert.course_name}</td>
                        <td>
                          <FaCalendar className="me-1 text-muted" />
                          {formatDate(cert.issue_date)}
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleViewCertificate(cert)}
                            title="View"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleDownloadCertificate(cert)}
                            title="Download"
                          >
                            <FaDownload />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedCertificate(cert);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">Delete Certificate</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCertificate && (
              <p>
                Are you sure you want to delete certificate{' '}
                <strong>{selectedCertificate.certificate_number}</strong> for{' '}
                <strong>{selectedCertificate.student_name}</strong>?
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteCertificate}>
              Delete Certificate
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CertificateManagement;